import { generatePopup } from '../../utils/popup'
import { firestore } from '../../firebase'
import { collection, doc, getDocs, getDoc } from 'firebase/firestore'
import { Action } from '../actionTypes';

// Initialize account state from localStorage
export const initializeAccountState: any = () => (dispatch: any) => {
    return new Promise(async (resolve, reject) => {
        try {
            const currentUserId = localStorage.getItem('userId');
            const activeAccountId = localStorage.getItem('activeAccountId') || currentUserId;
            const activeAccountType = localStorage.getItem('activeAccountType') || 'personal';
            const isAdminMode = localStorage.getItem('isAdminMode') === 'true';
            const originalAdminId = localStorage.getItem('originalAdminId');
            const adminPermissionMode = localStorage.getItem('adminPermissionMode') === 'true';
            const originalUserId = localStorage.getItem('originalUserId');
            
            console.log('initializeAccountState: Starting with localStorage values:', {
                currentUserId,
                activeAccountId,
                activeAccountType,
                isAdminMode,
                originalAdminId,
                adminPermissionMode,
                originalUserId
            });
            
            if (!currentUserId) {
                // No user logged in, skip initialization
                resolve(null);
                return;
            }
            
            // Set initial permissions based on account type
            let userPermissions = {
                billing: true,
                user_management: true,
                analytics: true,
                experience_creation: true,
                admin_experience_creation: false,
                create_products_markers: true
            };
            let userRole: 'primary' | 'sub-user' | null = null;
            
            if (activeAccountType === 'b2b' && activeAccountId !== currentUserId) {
                // Get permissions for this B2B account
                const effectiveUserId = originalUserId || currentUserId;
                console.log('initializeAccountState: Getting B2B permissions for activeAccountId:', activeAccountId, 'using userId:', effectiveUserId);
                
                const permissionDoc = await getDoc(doc(firestore, `users/${effectiveUserId}/account_permissions/${activeAccountId}`));
                if (permissionDoc.exists()) {
                    const permissionData = permissionDoc.data();
                    userPermissions = permissionData.permissions;
                    userRole = permissionData.role;
                    console.log('initializeAccountState: Found B2B permissions:', { userPermissions, userRole });
                } else {
                    console.warn('initializeAccountState: No B2B permissions found, using defaults');
                }
            } else {
                // Personal account - check if admin
                const targetUserId = activeAccountId || currentUserId;
                console.log('initializeAccountState: Getting personal account permissions for userId:', targetUserId);
                
                const userDoc = await getDoc(doc(firestore, 'users', targetUserId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    if (userData.protected?.isAdmin) {
                        userPermissions.admin_experience_creation = true;
                    }
                    console.log('initializeAccountState: Personal account isAdmin:', userData.protected?.isAdmin);
                }
            }
            
            // If in admin mode, override with admin permissions if adminPermissionMode is true
            if (isAdminMode && adminPermissionMode) {
                console.log('initializeAccountState: Applying admin permissions override');
                userPermissions = {
                    billing: true,
                    user_management: true,
                    analytics: true,
                    experience_creation: true,
                    admin_experience_creation: true,
                    create_products_markers: true
                };
            }
            
            dispatch({
                type: Action.INITIALIZE_ACCOUNT_STATE,
                payload: {
                    currentUserId,
                    activeAccountId,
                    activeAccountType,
                    isAdminMode,
                    originalAdminId,
                    adminPermissionMode,
                    originalUserId,
                    userPermissions,
                    userRole
                },
            });
            
            console.log('initializeAccountState: Successfully initialized state:', {
                activeAccountId,
                activeAccountType,
                userPermissions,
                userRole,
                originalUserId
            });
            
            resolve({ activeAccountId, activeAccountType, userPermissions, userRole });
        } catch (error: any) {
            console.error('Failed to initialize account state:', error);
            reject(error);
        }
    });
};

// Get user's available accounts and permissions
export const getAccountPermissions: any = () => (dispatch: any) => {
    return new Promise(async (resolve, reject) => {
        console.log('getAccountPermissions: Starting...')
        
        dispatch({
            type: Action.GET_ACCOUNT_PERMISSIONS_INIT,
        })
        
        try {
            const currentUserId = localStorage.getItem('originalUserId') || localStorage.getItem('userId');
            console.log('getAccountPermissions: currentUserId from localStorage:', currentUserId)
            
            if (!currentUserId) {
                throw new Error('No user logged in');
            }
            
            // Get account permissions for this user
            const permissionsPath = `users/${currentUserId}/account_permissions`
            console.log('getAccountPermissions: Querying path:', permissionsPath)
            
            const permissionsCollection = collection(firestore, permissionsPath);
            const querySnapshot = await getDocs(permissionsCollection);
            
            console.log('getAccountPermissions: Query returned', querySnapshot.size, 'documents')
            
            let availableAccounts: any[] = [];
            let currentPermissions = {
                billing: false,
                user_management: false,
                analytics: false,
                experience_creation: false,
                admin_experience_creation: false,
                create_products_markers: false
            };
            let currentRole = null;
            
            // Process B2B account permissions
            for (const docSnapshot of querySnapshot.docs) {
                const permissionData = docSnapshot.data();
                const businessId = docSnapshot.id;
                
                console.log('getAccountPermissions: Processing permission doc:', {
                    businessId,
                    permissionData
                })
                
                // Get business account details
                const businessDoc = await getDoc(doc(firestore, 'users', businessId));
                if (businessDoc.exists()) {
                    const businessData = businessDoc.data() as any;
                    console.log('getAccountPermissions: Found business data:', {
                        businessId,
                        businessData: {
                            businessName: businessData.businessName,
                            isB2B: businessData.protected?.isB2B
                        }
                    })
                    
                    availableAccounts.push({
                        accountId: businessId,
                        accountType: 'b2b',
                        businessName: businessData.businessName,
                        permissions: permissionData.permissions,
                        role: permissionData.role,
                        invited_by: permissionData.invited_by,
                        created: permissionData.created
                    });
                    if (localStorage.getItem('userId') === businessId) {
                      currentPermissions = permissionData.permissions
                    }
                } else {
                    console.warn('getAccountPermissions: Business document not found for ID:', businessId)
                }
            }
            
            
            // Check if user is admin for enhanced permissions on personal account
            const userDoc = await getDoc(doc(firestore, 'users', currentUserId));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                if (userData.protected?.isAdmin) {
                    currentPermissions.admin_experience_creation = true;
                }
            }
            
            dispatch({
                type: Action.GET_ACCOUNT_PERMISSIONS_SUCCESS,
                payload: {
                    availableAccounts,
                    currentPermissions,
                    currentRole
                },
            });
            
            console.log('getAccountPermissions: Success - dispatched results')
            resolve({ availableAccounts, currentPermissions, currentRole });
        } catch (error: any) {
            console.error('getAccountPermissions: Error:', error)
            dispatch({
                type: Action.GET_ACCOUNT_PERMISSIONS_FAIL,
                payload: error.message,
            });
            reject(error);
        }
    });
};

// Switch to a different account (B2B or back to personal)
export const switchAccount: any = (payload: any) => (dispatch: any) => {
    return new Promise(async (resolve, reject) => {
        dispatch({
            type: Action.SWITCH_ACCOUNT_INIT,
        })
        
        try {
            let { accountId, accountType } = payload;
            const currentUserId = localStorage.getItem('userId');
            const originalUserId = localStorage.getItem('originalUserId');
            
            console.log('switchAccount: Starting switch with payload:', { accountId, accountType, currentUserId, originalUserId });
            
            if (!currentUserId) {
                throw new Error('No user logged in');
            }
            
            let userPermissions = {
                billing: true,
                user_management: true,
                analytics: true,
                experience_creation: true,
                admin_experience_creation: false,
                create_products_markers: true
            };
            let userRole: 'primary' | 'sub-user' | null = null;
            
            if (accountType === 'b2b') {
                console.log('switchAccount: Switching to B2B account:', accountId);
                
                // Store original user ID if not already stored (for switching back)
                if (!originalUserId) {
                    console.log('switchAccount: Storing original user ID:', currentUserId);
                    localStorage.setItem('originalUserId', currentUserId);
                }
                
                // Get permissions for this B2B account
                const effectiveCurrentUserId = originalUserId || currentUserId;
                console.log('switchAccount: Getting permissions for B2B account using user ID:', effectiveCurrentUserId);
                
                const permissionDoc = await getDoc(doc(firestore, `users/${effectiveCurrentUserId}/account_permissions/${accountId}`));
                if (permissionDoc.exists()) {
                    const permissionData = permissionDoc.data();
                    userPermissions = permissionData.permissions;
                    userRole = permissionData.role;
                    console.log('switchAccount: Found B2B permissions:', { userPermissions, userRole });
                } else {
                    throw new Error('No permission to access this account');
                }
            } else {
                console.log('switchAccount: Switching to personal account:', accountId);
                
                // For personal account, use the original user ID if available, otherwise the provided account ID
                const targetUserId = originalUserId || accountId;
                console.log('switchAccount: Target personal user ID:', targetUserId);
                
                // Update accountId to the correct personal account ID
                accountId = targetUserId;
                
                const userDoc = await getDoc(doc(firestore, 'users', targetUserId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    if (userData.protected?.isAdmin) {
                        userPermissions.admin_experience_creation = true;
                    }
                    console.log('switchAccount: Personal account permissions set, isAdmin:', userData.protected?.isAdmin);
                } else {
                    throw new Error('Personal account not found');
                }
                
                // Clean up originalUserId since we're switching back to personal
                if (originalUserId) {
                    console.log('switchAccount: Cleaning up originalUserId from localStorage');
                    localStorage.removeItem('originalUserId');
                }
            }
            
            console.log('switchAccount: Setting localStorage values:', {
                activeAccountId: accountId,
                activeAccountType: accountType,
                userId: accountId
            });
            
            // Update localStorage to reflect active account
            localStorage.setItem('activeAccountId', accountId);
            localStorage.setItem('activeAccountType', accountType);
            
            // CRITICAL: Update userId to point to the active account for data fetching
            localStorage.setItem('userId', accountId);
            
            const finalOriginalUserId = accountType === 'personal' ? null : (originalUserId || currentUserId);
            
            dispatch({
                type: Action.SWITCH_ACCOUNT_SUCCESS,
                payload: {
                    activeAccountId: accountId,
                    activeAccountType: accountType,
                    userPermissions,
                    userRole,
                    originalUserId: finalOriginalUserId
                },
            });
            
            console.log('switchAccount: Success, dispatched final state:', {
                activeAccountId: accountId,
                activeAccountType: accountType,
                userPermissions,
                userRole,
                originalUserId: finalOriginalUserId
            });
            
            generatePopup('success', 'Account switched successfully');
            resolve({ accountId, accountType, userPermissions, userRole });
        } catch (error: any) {
            console.error('switchAccount: Error occurred:', error);
            dispatch({
                type: Action.SWITCH_ACCOUNT_FAIL,
                payload: error.message,
            });
            generatePopup('error', error.message);
            reject(error);
        }
    });
};

// Enhanced admin switching with proper Redux state
export const adminSwitchToAccount: any = (payload: any) => (dispatch: any) => {
    return new Promise(async (resolve, reject) => {
        dispatch({
            type: Action.SWITCH_ACCOUNT_INIT,
        })
        
        try {
            const { targetAccountId } = payload;
            const adminUserId = localStorage.getItem('userId');
            
            if (!adminUserId) {
                throw new Error('No admin logged in');
            }
            
            // Verify admin status
            const adminDoc = await getDoc(doc(firestore, 'users', adminUserId));
            if (!adminDoc.exists() || !adminDoc.data().protected?.isAdmin) {
                throw new Error('Insufficient permissions');
            }
            
            // Get target account data
            const targetDoc = await getDoc(doc(firestore, 'users', targetAccountId));
            if (!targetDoc.exists()) {
                throw new Error('Target account not found');
            }
            
            const targetData = targetDoc.data();
            const isB2B = targetData.protected?.isB2B || false;
            
            // Store admin state for reverting
            if (!localStorage.getItem('originalAdminId')) {
                localStorage.setItem('originalAdminId', adminUserId);
            }
            localStorage.setItem('userId', targetAccountId);
            localStorage.setItem('activeAccountId', targetAccountId);
            localStorage.setItem('activeAccountType', isB2B ? 'b2b' : 'personal');
            localStorage.setItem('isAdminMode', 'true');
            localStorage.setItem('adminPermissionMode', 'true');
            
            // Admin gets full permissions
            const adminPermissions = {
                billing: true,
                user_management: true,
                analytics: true,
                experience_creation: true,
                admin_experience_creation: true,
                create_products_markers: true
            };
            
            dispatch({
                type: Action.SWITCH_ACCOUNT_SUCCESS,
                payload: {
                    activeAccountId: targetAccountId,
                    activeAccountType: isB2B ? 'b2b' : 'personal',
                    userPermissions: adminPermissions,
                    userRole: 'admin',
                    isAdminMode: true,
                    originalAdminId: adminUserId,
                    adminPermissionMode: true
                },
            });
            
            generatePopup('success', 'Switched to account successfully');
            resolve({ targetAccountId, adminPermissions });
        } catch (error: any) {
            dispatch({
                type: Action.SWITCH_ACCOUNT_FAIL,
                payload: error.message,
            });
            generatePopup('error', error.message);
            reject(error);
        }
    });
};

// Switch back to admin account
export const switchBackToAdmin: any = () => (dispatch: any) => {
    return new Promise(async (resolve, reject) => {
        dispatch({
            type: Action.SWITCH_BACK_TO_ADMIN_INIT,
        })
        
        try {
            const originalAdminId = localStorage.getItem('originalAdminId');
            
            if (!originalAdminId) {
                throw new Error('No admin session found');
            }
            
            // Restore admin session
            localStorage.setItem('userId', originalAdminId);
            localStorage.setItem('activeAccountId', originalAdminId);
            localStorage.setItem('activeAccountType', 'personal');
            localStorage.removeItem('originalAdminId');
            localStorage.removeItem('isAdminMode');
            localStorage.removeItem('adminPermissionMode');
            
            dispatch({
                type: Action.SWITCH_BACK_TO_ADMIN_SUCCESS,
                payload: {
                    currentUserId: originalAdminId,
                    activeAccountId: originalAdminId
                },
            });
            
            generatePopup('success', 'Returned to admin account');
            resolve(originalAdminId);
        } catch (error: any) {
            dispatch({
                type: Action.SWITCH_BACK_TO_ADMIN_FAIL,
                payload: error.message,
            });
            generatePopup('error', error.message);
            reject(error);
        }
    });
};

// Toggle admin permission mode when viewing another account
export const toggleAdminPermissionMode: any = () => (dispatch: any) => {
    return new Promise(async (resolve, reject) => {
        try {
            const isAdminMode = localStorage.getItem('isAdminMode') === 'true';
            const activeAccountId = localStorage.getItem('activeAccountId');
            const activeAccountType = localStorage.getItem('activeAccountType') || 'personal';
            const currentAdminPermissionMode = localStorage.getItem('adminPermissionMode') === 'true';
            
            if (!isAdminMode || !activeAccountId) {
                throw new Error('Not in admin mode or no active account');
            }
            
            // Toggle the permission mode
            const newAdminPermissionMode = !currentAdminPermissionMode;
            localStorage.setItem('adminPermissionMode', newAdminPermissionMode.toString());
            
            let userPermissions;
            let userRole: 'primary' | 'sub-user' | null = null;
            
            if (newAdminPermissionMode) {
                // Use full admin permissions
                userPermissions = {
                    billing: true,
                    user_management: true,
                    analytics: true,
                    experience_creation: true,
                    admin_experience_creation: true,
                    create_products_markers: true
                };
            } else {
                // Use the target user's actual permissions
                if (activeAccountType === 'b2b') {
                    // Get the B2B account's permissions for this user if they were a normal user
                    const originalUserId = localStorage.getItem('originalAdminId');
                    if (originalUserId) {
                        try {
                            const permissionDoc = await getDoc(doc(firestore, `users/${originalUserId}/account_permissions/${activeAccountId}`));
                            if (permissionDoc.exists()) {
                                const permissionData = permissionDoc.data();
                                userPermissions = permissionData.permissions;
                                userRole = permissionData.role;
                            } else {
                                // No specific permissions found, use default B2B permissions
                                userPermissions = {
                                    billing: false,
                                    user_management: false,
                                    analytics: true,
                                    experience_creation: true,
                                    admin_experience_creation: false,
                                    create_products_markers: true
                                };
                                userRole = 'sub-user' as 'sub-user';
                            }
                        } catch (error) {
                            // Fallback to default permissions
                            userPermissions = {
                                billing: false,
                                user_management: false,
                                analytics: true,
                                experience_creation: true,
                                admin_experience_creation: false,
                                create_products_markers: true
                            };
                            userRole = 'sub-user' as 'sub-user';
                        }
                    }
                } else {
                    // Personal account - get the user's normal permissions
                    const targetUserDoc = await getDoc(doc(firestore, 'users', activeAccountId));
                    if (targetUserDoc.exists()) {
                        const userData = targetUserDoc.data();
                        userPermissions = {
                            billing: true,
                            user_management: true,
                            analytics: true,
                            experience_creation: true,
                            admin_experience_creation: userData.protected?.isAdmin || false,
                            create_products_markers: true
                        };
                    } else {
                        // Fallback to basic user permissions
                        userPermissions = {
                            billing: true,
                            user_management: true,
                            analytics: true,
                            experience_creation: true,
                            admin_experience_creation: false,
                            create_products_markers: true
                        };
                    }
                }
            }
            
            dispatch({
                type: Action.TOGGLE_ADMIN_PERMISSION_MODE,
                payload: {
                    adminPermissionMode: newAdminPermissionMode,
                    userPermissions,
                    userRole
                },
            });
            
            generatePopup('success', `Switched to ${newAdminPermissionMode ? 'Admin' : 'User'} permissions`);
            resolve({ adminPermissionMode: newAdminPermissionMode, userPermissions, userRole });
        } catch (error: any) {
            generatePopup('error', error.message);
            reject(error);
        }
    });
}; 